/* eslint-disable react/prop-types */
import React, { useState, useMemo, useEffect } from 'react';
import { Button, Card, Status } from '@insight-health-ai/roseate/components';
import { Tooltip } from 'antd';
import {
  Edit02,
  X,
  Check,
  Plus,
  MessageTextSquare01,
  AlertTriangle,
  ChevronLeft,
  Calendar,
  HelpCircle,
} from '@insight-health-ai/roseate/icons';
import {
  useGetSummaryList,
  useUpdateMedicineSummary,
  useDeleteMedicineSummary,
} from 'src/hooks/summary';
import { ScreenLoader } from 'src/components';
import { getLatestSummary } from 'src/utils/summary/helpers';
import SavePrescriptionReviewModal from './SavePrescriptionReviewModal';
import { showToastSuccess } from 'src/utilities/toast';
import { showToastError } from 'src/utilities/errortoast';

const PrescriptionReview = ({
  onBack,
  encounterId,
  onApprovePrescription,
  approvedOrders,
  rereviewOrderIds,
  medicineData,
  isLoadingMedicines,
}) => {
  // API hooks
  // Fetch SNOMED data for this encounter
  const { data: snomedData } = useGetSummaryList({
    encounterId,
    encounterPhase: 'in-visit',
    type: 'snomed',
  });

  // Memoized SNOMED codes from the latest summary version
  const snomedCodes = useMemo(() => {
    if (!snomedData?.summaries) return [];

    const latestSummary = getLatestSummary(snomedData.summaries);
    if (!latestSummary) return [];

    try {
      // Safely parse SNOMED codes from the summary JSON
      return JSON.parse(latestSummary.summary_json || '[]');
    } catch (error) {
      console.error('Error parsing SNOMED summary_json:', error);
      return [];
    }
  }, [snomedData]);

  const updateMedicineMutation = useUpdateMedicineSummary();
  const deleteMedicineMutation = useDeleteMedicineSummary();

  // Memoized prescriptions from the latest medicine summary
  const prescriptionsFromAPI = useMemo(() => {
    if (!medicineData?.summaries) return [];

    // Find the latest version summary
    const latestSummary = getLatestSummary(medicineData.summaries);

    if (!latestSummary) return [];

    let allPrescriptions = [];

    try {
      // Parse the summary_json string to get medication array from latest version
      const medications = JSON.parse(latestSummary.summary_json || '[]').filter(
        (med) => !med.is_deleted
      );

      medications.forEach((medication, medIndex) => {
        allPrescriptions.push({
          id: `${latestSummary.summary_id}-${medIndex}`,
          name: medication.medication_name || 'Unknown Medication',
          confidence: Math.round((medication.confidence || 0.85) * 100), // Convert to percentage
          condition: medication.evidence || 'Medical condition',
          dosage: medication.dosage || 'As prescribed',
          dosage_unit: medication.dosage_unit || 'As prescribed',
          frequency: medication.frequency || 'As directed',
          duration: medication.duration || 'As directed',
          associated_diagnosis_code: medication.icd10_code || '',
          associated_diagnosis_description:
            medication.snomed_code_description || 'Custom diagnosis',
          warnings: (() => {
            if (!medication.safety_alerts) return ['Monitor as directed'];

            // Handle nested arrays like [["Do not exceed..."]]
            if (Array.isArray(medication.safety_alerts)) {
              const flatAlerts = medication.safety_alerts.flat();
              return flatAlerts.length > 0
                ? flatAlerts
                : ['Monitor as directed'];
            }

            // Handle string format
            if (typeof medication.safety_alerts === 'string') {
              return [medication.safety_alerts];
            }

            return ['Monitor as directed'];
          })(),
          historicalPattern: medication.historical_pattern || '',
          snomed_code: medication.snomed_code || '',
          order_type_id: medication.order_type_id || '',
        });
      });
    } catch (error) {
      console.error('Error parsing summary_json from latest version:', error);
      // Fallback for malformed JSON
      allPrescriptions = [];
    }

    return allPrescriptions;
  }, [medicineData]);

  const [prescriptions, setPrescriptions] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPrescription, setEditingPrescription] = useState(null);

  // Update prescriptions when API data is loaded
  useEffect(() => {
    if (prescriptionsFromAPI.length > 0) {
      const approvedOrderIds = new Set(
        approvedOrders.map((order) => order.order_type_id)
      );
      const filteredPrescriptions = prescriptionsFromAPI.filter(
        (p) =>
          !approvedOrderIds.has(p.order_type_id) ||
          rereviewOrderIds.has(p.order_type_id)
      );
      setPrescriptions(filteredPrescriptions);
    }
  }, [prescriptionsFromAPI, approvedOrders, rereviewOrderIds]);

  /**
   * Approves a prescription, moving it from pending to the parent component's approved list.
   * @param {string} prescriptionId - The ID of the prescription to approve.
   */
  const handleApprove = (prescriptionId) => {
    const prescription = prescriptions.find((p) => p.id === prescriptionId);
    if (prescription && onApprovePrescription) {
      // Add to approved orders in parent component
      onApprovePrescription(prescription);

      // Remove from pending prescriptions
      setPrescriptions((prev) => prev.filter((p) => p.id !== prescriptionId));

      showToastSuccess(`Prescription ${prescription.name} approved`);
    }
  };

  /**
   * Opens the edit modal with the selected prescription's data.
   * @param {string} prescriptionId - The ID of the prescription to edit.
   */
  const handleEdit = (prescriptionId) => {
    const prescriptionToEdit = prescriptions.find(
      (p) => p.id === prescriptionId
    );
    if (prescriptionToEdit) {
      // Transform prescription data for the form
      const formValues = {
        ...prescriptionToEdit,
        associated_diagnosis_code:
          prescriptionToEdit.associated_diagnosis_code || '',
        snomed_code: prescriptionToEdit.snomed_code || '',
        snomed_code_description:
          prescriptionToEdit.snomed_code_description || 'Custom diagnosis',
        order_type_id: prescriptionToEdit.order_type_id,
        dosage_unit: prescriptionToEdit.dosage_unit || 'As prescribed',
        frequency: prescriptionToEdit.frequency || 'As directed',
        duration: prescriptionToEdit.duration || 'As directed',
        warnings: Array.isArray(prescriptionToEdit.warnings)
          ? prescriptionToEdit.warnings.join(', ')
          : prescriptionToEdit.warnings || '',
      };
      setEditingPrescription(formValues);
      setIsModalOpen(true);
    }
  };

  /**
   * Rejects a prescription, deleting it from the backend and removing it from the local state.
   * @param {string} prescriptionId - The ID of the prescription to reject.
   */
  const handleReject = async (prescriptionId) => {
    const prescription = prescriptions.find((p) => p.id === prescriptionId);
    if (prescription && prescription.order_type_id) {
      try {
        // Call delete API for prescriptions with order_type_id
        await deleteMedicineMutation.mutateAsync({
          encounterId,
          medicines: [{ order_type_id: prescription.order_type_id }],
        });
      } catch (error) {
        showToastError(`Failed to reject prescription ${prescription.name}`);
      }
    }

    // Remove from pending prescriptions (local state)
    setPrescriptions((prev) => prev.filter((p) => p.id !== prescriptionId));
    showToastSuccess(`Prescription ${prescription.name} rejected`);
  };

  /**
   * Opens the modal to add a new custom prescription.
   */
  const handleAddCustom = () => {
    setEditingPrescription(null);
    setIsModalOpen(true);
  };

  /**
   * Closes the prescription form modal and resets the editing state.
   */
  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingPrescription(null);
  };

  /**
   * Saves or updates a prescription, calling the appropriate API and updating the local state.
   * @param {Object} values - The prescription data from the form.
   */
  const handleSavePrescription = (values, orderTypeId) => {
    const updatedPrescriptions = editingPrescription
      ? prescriptions.map((p) =>
          p.id === editingPrescription.id
            ? {
                ...p,
                ...values,
                dosage: values.dosage || 'As prescribed',
                dosage_unit: values.dosage_unit,
                frequency: values.frequency || 'As directed',
                duration: values.duration || 'As directed',
                order_type_id: values.order_type_id,
                associated_diagnosis_code:
                  values.associated_diagnosis_code || '',
                snomed_code: values.snomed_code || '',
                snomed_code_description:
                  values.snomed_code_description || 'Custom diagnosis',
                associated_diagnosis_description:
                  values.snomed_code_description || 'Custom diagnosis',
                warnings: (() => {
                  if (!values.warnings) return p.warnings || [];
                  if (Array.isArray(values.warnings)) return values.warnings;
                  if (typeof values.warnings === 'string')
                    return values.warnings.split(',');
                  return p.warnings || [];
                })(),
              }
            : p
        )
      : [
          ...prescriptions,
          {
            id: `custom-${Date.now()}`,
            ...values,
            confidence: 100,
            dosage: values.dosage || 'As prescribed',
            dosage_unit: values.dosage_unit,
            order_type_id: orderTypeId,
            warnings: (() => {
              if (!values.warnings) return [];
              if (Array.isArray(values.warnings)) return values.warnings;
              if (typeof values.warnings === 'string')
                return values.warnings.split(',');
              return [];
            })(),
            frequency: values.frequency || 'As directed',
            duration: values.duration || 'As directed',
          },
        ];
    setPrescriptions(updatedPrescriptions);
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 90) return 'text-green-600';
    if (confidence >= 80) return 'text-blue-600';
    if (confidence >= 70) return 'text-yellow-600';
    return 'text-red-600';
  };

  // Show loading spinner while fetching data or during mutations
  if (
    isLoadingMedicines ||
    updateMedicineMutation.isPending ||
    deleteMedicineMutation.isPending
  ) {
    return <ScreenLoader />;
  }

  return (
    <div className="h-full w-full bg-white overflow-y-auto">
      {/* Header */}
      <div className="border-b border-gray-200 p-4">
        <div className="flex items-center gap-3 mb-3">
          <button
            onClick={onBack}
            className="text-gray-500 hover:text-gray-700"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <MessageTextSquare01 className="w-5 h-5 text-teal-500" />
            <h1 className="text-xl font-semibold text-gray-900">
              AI Prescription Review
            </h1>
          </div>
        </div>

        <div className="text-sm text-gray-500">
          Review and manage AI-suggested prescriptions
        </div>
      </div>

      {/* Content */}
      <div className="p-3 max-w-4xl">
        {prescriptions.length === 0 ? (
          <div className="text-center py-8">
            <MessageTextSquare01 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {medicineData?.summaries?.length === 0
                ? 'No prescriptions found'
                : 'All prescriptions reviewed!'}
            </h3>
            <p className="text-gray-500 mb-4">
              {medicineData?.summaries?.length === 0
                ? 'No medicine summaries were found for this encounter.'
                : 'All prescriptions have been approved or rejected.'}
            </p>
            <Button
              appearance="primary"
              onClick={onBack}
              className="bg-teal-600 hover:bg-teal-700"
            >
              Back to Orders
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {prescriptions.map((prescription) => (
              <Card key={prescription.id} className="border border-gray-200">
                <div className="p-1">
                  {/* Header with drug name and confidence */}
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <MessageTextSquare01 className="w-4 h-4 text-teal-500" />
                      <h3 className="font-semibold text-gray-900">
                        {prescription.name}
                      </h3>
                    </div>
                    <Tooltip
                      title={
                        <div style={{ width: '280px', fontSize: '12px' }}>
                          <div
                            style={{ fontWeight: '600', marginBottom: '4px' }}
                          >
                            Confidence Score
                          </div>
                          <div style={{ marginBottom: '8px' }}>
                            AI confidence level based on:
                          </div>
                          <ul
                            style={{ marginLeft: '16px', marginBottom: '8px' }}
                          >
                            <li>Patient symptoms and evidence</li>
                            <li>Medical history patterns</li>
                            <li>Clinical guidelines</li>
                          </ul>
                          <div>
                            <div
                              style={{ color: '#16a34a', marginBottom: '2px' }}
                            >
                              80-100%: High confidence
                            </div>
                            <div
                              style={{ color: '#ca8a04', marginBottom: '2px' }}
                            >
                              60-79%: Moderate confidence
                            </div>
                            <div style={{ color: '#dc2626' }}>
                              Below 60%: Low confidence
                            </div>
                          </div>
                        </div>
                      }
                      placement="left"
                      overlayStyle={{ maxWidth: '300px' }}
                    >
                      <div className="cursor-help">
                        <Status
                          size="xs"
                          label={
                            <div className="flex items-center gap-1">
                              <span>{prescription.confidence}%</span>
                              <HelpCircle className="w-3 h-3" />
                            </div>
                          }
                          appearance={
                            prescription.confidence >= 80
                              ? 'success'
                              : prescription.confidence >= 60
                                ? 'warning'
                                : 'error'
                          }
                          className="text-xs font-semibold"
                        />
                      </div>
                    </Tooltip>
                  </div>

                  {/* Condition */}
                  <div className="text-sm text-gray-600 mb-3">
                    {prescription.condition}
                  </div>

                  {/* Prescription details grid */}
                  <div className="grid grid-cols-3 gap-3 mb-3">
                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                        Dosage
                      </div>
                      <div className="text-sm font-medium">
                        {prescription.dosage}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                        Frequency
                      </div>
                      <div className="text-sm font-medium">
                        {prescription.frequency}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                        Duration
                      </div>
                      <div className="text-sm font-medium">
                        {prescription.duration}
                      </div>
                    </div>
                  </div>

                  {/* Associated Diagnoses */}
                  <div className="mb-3">
                    <div className="text-xs text-gray-500 uppercase tracking-wide mb-2">
                      Associated Diagnosis
                    </div>
                    {prescription.associated_diagnosis_code && (
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-mono text-teal-600 bg-teal-50 px-2 py-1 rounded text-xs">
                          {prescription.associated_diagnosis_code}
                        </span>
                        <span className="text-sm text-gray-600">
                          {prescription.associated_diagnosis_description}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Warnings */}
                  <div className="mb-3">
                    <div className="flex items-center gap-1 mb-2">
                      <AlertTriangle className="w-4 h-4 text-yellow-500" />
                      <span className="text-xs text-gray-500 uppercase tracking-wide">
                        Warnings
                      </span>
                    </div>
                    <div className="bg-yellow-50 border border-yellow-200 rounded p-2">
                      {(prescription.warnings || []).map((warning, index) => (
                        <div key={index} className="text-sm text-yellow-700">
                          • {warning}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="flex items-center gap-3">
                    <Button
                      appearance="primary"
                      size="sm"
                      icon={<Check className="w-4 h-4" />}
                      onClick={() => handleApprove(prescription.id)}
                      className="bg-green-600 hover:bg-green-700 text-white font-medium px-4 py-2 min-w-[100px]"
                    >
                      Approve
                    </Button>
                    <Button
                      variant="outlined"
                      appearance="secondary"
                      size="sm"
                      icon={<Edit02 className="w-4 h-4" />}
                      onClick={() => handleEdit(prescription.id)}
                      className="border-gray-300 !text-gray-700 hover:!bg-gray-50 hover:!border-gray-400 font-medium px-4 py-2 min-w-[100px]"
                    >
                      Edit
                    </Button>
                    <Button
                      variant="outlined"
                      appearance="secondary"
                      size="sm"
                      icon={<X className="w-4 h-4" />}
                      onClick={() => handleReject(prescription.id)}
                      className="border-red-300 !text-red-600 hover:!bg-red-50 hover:!border-red-400 font-medium px-4 py-2 min-w-[100px]"
                    >
                      Reject
                    </Button>
                  </div>
                </div>
              </Card>
            ))}

            {/* Add Custom Prescription - Now part of the grid */}
            <Card className="border border-gray-200 border-dashed flex items-center justify-center min-h-[300px]">
              <div className="text-center p-3">
                <MessageTextSquare01 className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <h3 className="font-semibold text-gray-900 mb-2">
                  Add Custom Prescription
                </h3>
                <p className="text-sm text-gray-500 mb-3">
                  Add a prescription that wasn't suggested by AI or modify
                  existing recommendations
                </p>
                <Button
                  appearance="primary"
                  size="sm"
                  icon={<Plus className="w-3 h-3" />}
                  onClick={handleAddCustom}
                  className="bg-teal-600 hover:bg-teal-700"
                >
                  Add Prescription
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>
      <SavePrescriptionReviewModal
        open={isModalOpen}
        onClose={handleModalClose}
        editingPrescription={editingPrescription}
        encounterId={encounterId}
        onSave={handleSavePrescription}
      />
    </div>
  );
};

export default PrescriptionReview;
